import { useState, useEffect, useCallback } from 'react';
import { APP_VERSION } from '../constants';

interface VersionInfo {
  version: string;
  timestamp: number;
}

interface UseVersionCheckOptions {
  checkInterval: number; // milliseconds, default 5 minutes
  enabled: boolean; // whether to enable version checking
}

export const useVersionCheck = (options: UseVersionCheckOptions) => {
	const { checkInterval, enabled } = options;
  
	const [currentVersion] = useState<string>(APP_VERSION);
	const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
	const [isChecking, setIsChecking] = useState(false);

	const checkVersion = useCallback(async () => {
		if (!enabled) return;
    
		setIsChecking(true);
    
		try {
			const response = await fetch('/api/version', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
				// Add cache-busting to ensure we get fresh data
				cache: 'no-cache',
			});
      
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
      
			const versionInfo: VersionInfo = await response.json();
      
			if (versionInfo.version !== currentVersion) {
				setIsNewVersionAvailable(true);
			} else {
				setIsNewVersionAvailable(false);
			}
      
		} catch (err) {
			console.error('Failed to check version:', err);
		} finally {
			setIsChecking(false);
		}
	}, [enabled, currentVersion]);

	// Initial check on mount
	useEffect(() => {
		if (enabled) {
			checkVersion();
		}
	}, [enabled, checkVersion]);

	// Set up periodic checking
	useEffect(() => {
		if (!enabled) return;
    
		const interval = setInterval(checkVersion, checkInterval);
    
		return () => clearInterval(interval);
	}, [enabled, checkInterval, checkVersion]);

	return {
		isNewVersionAvailable,
		isChecking,
	};
}; 